import {
    Box, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Autocomplete,
    Grid
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
// import SearchIcon from '@mui/icons-material/Search'
import LedgerLogo from "../../assets/Images/ledger.png"
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiGetLedger } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'

interface LedgerEntry {
    _id: string
    type: 'DEBIT' | 'CREDIT'
    referenceType: string
    account: string
    counterparty: string
    amount: number
    executedAmount: number
    orderFee: number
    platformFee: number
    orderSide: string | null
    orderType: string | null
    sfoxStatus: string | null
    currency: string
    description: string
    status: 'PENDING' | 'COMPLETED' | 'REVERSED' | 'FAILED'
    runningBalance: number
    createdAt: string
    linkedEntryId?: {
        type: string
        account: string
        amount: number
        status: string
    } | null
}

interface PendingInfo {
    count: number
    total: number
}

interface Summary {
    ledger: {
        deposits: number
        withdrawals: number
        repayments: number
        interest: number
        refunds: number
        fees: number
        cashouts: number
        totalIn: number
        totalOut: number
    }
    trading: {
        directBuyTotal: number
        loanBuyTotal: number
        loanBuyUserAmount: number
        sellTotal: number
        adjustmentTotal: number
        adjustmentFees: number
        buyFees: number
        sellFees: number
        platformFee: number
        buyPlatformFee: number
        sellPlatformFee: number
        loanFee: number
        loanPlatformFee: number
        totalOrderFees: number
    }
    pending: {
        deposits: PendingInfo
        withdrawals: PendingInfo
    }
}
interface Wallets {
    usd?: {
        totalBalance: number
        availableBalance: number
        withdrawableBalance: number
        totalACHHeld: number
    }
}

const TYPE_OPTIONS = ['DEBIT', 'CREDIT']
const STATUS_OPTIONS = ['PENDING', 'COMPLETED', 'REVERSED', 'FAILED']
const FIAT_CURRENCIES = ['USD', 'AUD']

const isFiatCurrency = (currency: string) =>
    FIAT_CURRENCIES.includes(currency?.toUpperCase())

const formatCryptoAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })

const getEntryAmount = (entry: LedgerEntry) =>
    (entry.referenceType === 'ORDER' || entry.referenceType === 'ADJUSTMENT')
        // ? entry.executedAmount
        ? entry.amount
        : entry.amount

const formatCurrencyAmount = (entry: LedgerEntry) => {
    const value = getEntryAmount(entry)
    const sign = entry.type === 'CREDIT' ? '+' : '-'
    return isFiatCurrency(entry.currency)
        ? `${sign}$${formatAmount(value)}`
        : `${sign}${formatCryptoAmount(value)} ${entry.currency}`
}

const formatAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const Ledgers: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const userId = location.state?.userId
    const firstName = location.state?.firstName
    const lastName = location.state?.lastName
    const email = location.state?.email
    const account = location.state?.account

    const [entries, setEntries] = useState<LedgerEntry[]>([])
    const [summary, setSummary] = useState<Summary | null>(null)
    // const [search, setSearch] = useState('')
    const [type, setType] = useState('')
    const [status, setStatus] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [totalRows, setTotalRows] = useState(0)
    const [wallets, setWallets] = useState<Wallets | null>(null)
    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    })

    const fetchLedger = useCallback(async () => {
        try {
            const params = new URLSearchParams()
            if (userId) params.append('userId', userId)
            if (type) params.append('type', type)
            if (status) params.append('status', status)
            if (account) params.append('account', account)
            // if (search) params.append('search', search)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))

            const res = await ApiGetLedger(`?${params.toString()}`)
            if (res?.status) {
                setEntries(res.data.entries)
                setSummary(res.data.summary)
                setWallets(res.data.wallets)
                setTotalRows(res.data.pagination.total)
            }
        } catch (error: any) {
            console.error('Error fetching ledger:', error)
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to fetch ledger',
            })
        }
    }, [userId, type, status, page, rowsPerPage, account])

    useEffect(() => {
        fetchLedger()
    }, [fetchLedger])

    const formatAmount = (amount: number) =>
        amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    const formatDate = (date: string) =>
        new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

    const getStatusChip = (status: string) => {
        const map: Record<string, { color: string; bg: string; border: string }> = {
            COMPLETED: { color: '#10b981', bg: '#10b98120', border: '#10b981' },
            PENDING: { color: '#f59e0b', bg: '#f59e0b20', border: '#f59e0b' },
            REVERSED: { color: '#3b82f6', bg: '#3b82f620', border: '#3b82f6' },
            FAILED: { color: '#ef4444', bg: '#ef444420', border: '#ef4444' },
        }
        const s = map[status] || { color: '#9a9a9a', bg: '#9a9a9a20', border: '#9a9a9a' }
        return (
            <Box sx={{
                display: 'inline-block', px: 1.5, py: 0.4,
                borderRadius: '5px', fontSize: '11px', fontWeight: 600,
                color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`
            }}>
                {status}
            </Box>
        )
    }

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate(-1)}>
                        <ArrowBackIcon sx={{ color: '#ffffff' }} />
                    </IconButton>
                    <Box className="p-2 bg-light-white border-10">
                        <img src={LedgerLogo} alt='ledger' height={'28px'} />
                    </Box>
                    <Typography className='fs-24 text-white fw-medium'>Ledger</Typography>
                </Box>
                <Typography className='fs-24 text-white fw-medium'>
                    {firstName} {lastName} ({email})
                </Typography>
            </Box>

            {summary && (
                <Grid container spacing={2} mb={4}>
                    {[
                        { label: 'Total In', value: `$${formatAmount(summary.ledger.totalIn)}`, color: '#10b981' },
                        { label: 'Total Out', value: `$${formatAmount(summary.ledger.totalOut)}`, color: '#ef4444' },
                        { label: 'Total Wallet Balance', value: `$${formatAmount(wallets?.usd?.totalBalance ?? 0)}`, color: (wallets?.usd?.totalBalance ?? 0) >= 0 ? '#10b981' : '#ef4444' },
                        { label: 'Available Wallet Balance', value: `$${formatAmount(wallets?.usd?.availableBalance ?? 0)}`, color: (wallets?.usd?.availableBalance ?? 0) >= 0 ? '#10b981' : '#ef4444' },
                        { label: 'Withdrawable', value: `$${formatAmount(wallets?.usd?.withdrawableBalance ?? 0)}`, color: '#3b82f6' },
                        { label: 'Deposits', value: `$${formatAmount(summary.ledger.deposits)}`, color: '#10b981' },
                        { label: 'Withdrawals', value: `$${formatAmount(summary.ledger.withdrawals)}`, color: '#ef4444' },
                        { label: 'Cashouts', value: `$${formatAmount(summary.ledger.cashouts)}`, color: '#10b981' },
                        { label: 'Loan Buy', value: `$${formatAmount(summary.trading.loanBuyTotal)}`, color: '#a78bfa' },
                        { label: 'Direct Buy', value: `$${formatAmount(summary.trading.directBuyTotal)}`, color: '#3b82f6' },
                        { label: 'Sell', value: `$${formatAmount(summary.trading.sellTotal)}`, color: '#10b981' },
                        // { label: 'Interest', value: `$${formatAmount(summary.ledger.interest)}`, color: '#f59e0b' },
                        { label: 'Order Fees', value: `$${formatAmount(summary.trading.totalOrderFees)}`, color: '#f59e0b' },
                        { label: 'Pending Deposits', value: `$${formatAmount(summary.pending.deposits.total)}`, sub: `${summary.pending.deposits.count} entries`, color: '#f59e0b' },
                        { label: 'Pending Withdrawals', value: `$${formatAmount(summary.pending.withdrawals.total)}`, sub: `${summary.pending.withdrawals.count} entries`, color: '#ef4444' },
                        { label: 'Selldown', value: `$${formatAmount(summary.trading.adjustmentTotal)}`, sub: `fees: $${formatAmount(summary.trading.adjustmentFees)}`, color: '#ef4444' },
                    ].map((s) => (
                        <Grid key={s.label} size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <Box className="border-10 p-3 px-4 bg-dark-color" sx={{ height: '100%' }}>
                                <Typography fontSize={11} color="#6f6f6f" textTransform="uppercase" mb={0.5}>{s.label}</Typography>
                                <Typography fontSize={18} fontWeight={700} color={s.color}>{s.value}</Typography>
                                {s.sub && <Typography fontSize={10} color="#6f6f6f" mt={0.5}>{s.sub}</Typography>}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
            <Box className="bg-dark-color border-10 p-4">
                <Box className="d-flex align-items-center gap-3 mb-4" sx={{ flexWrap: 'wrap' }}>
                    {/* <TextField
                        placeholder="Search description..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                        size="small"
                        sx={searchFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className='color-primary' />
                                </InputAdornment>
                            ),
                        }}
                    /> */}
                    <Autocomplete
                        size="small"
                        value={type}
                        disableClearable={!type}
                        onChange={(_, newValue) => { setType(newValue || ''); setPage(0) }}
                        options={TYPE_OPTIONS}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Dr / Cr" sx={autocompleteFieldStyle}
                                InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                            />
                        )}
                        sx={{ width: 160 }}
                        PaperComponent={({ children }) => (
                            <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                {children}
                            </Box>
                        )}
                    />
                    <Autocomplete
                        size="small"
                        value={status}
                        disableClearable={!status}
                        onChange={(_, newValue) => { setStatus(newValue || ''); setPage(0) }}
                        options={STATUS_OPTIONS}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Status" sx={autocompleteFieldStyle}
                                InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                            />
                        )}
                        sx={{ width: 180 }}
                        PaperComponent={({ children }) => (
                            <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                {children}
                            </Box>
                        )}
                    />
                </Box>

                <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: 'none' }, '& .MuiTableRow-root': { borderBottom: 'none' } }}>
                        <TableHead>
                            <TableRow>
                                {['No', 'Date', 'Type', 'Reference', 'Account → Counterparty', 'Amount', 'Description', 'Status'].map((head) => (
                                    <TableCell key={head} className='color-primary' sx={{ backgroundColor: '#241815', color: '#ff6a3d', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.length > 0 ? entries.map((entry, index) => (
                                <TableRow key={entry._id}>
                                    <TableCell className='text-white py-1'>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell className='text-white py-1' sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.createdAt)}</TableCell>
                                    <TableCell className='py-1'>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                            fontSize: '11px', fontWeight: 700,
                                            color: entry.type === 'CREDIT' ? '#10b981' : '#ef4444',
                                            backgroundColor: entry.type === 'CREDIT' ? '#10b98120' : '#ef444420',
                                            border: `1px solid ${entry.type === 'CREDIT' ? '#10b981' : '#ef4444'}`
                                        }}>
                                            {entry.type}
                                        </Box>
                                    </TableCell>
                                    <TableCell className='py-1'>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                            fontSize: '10px', fontWeight: 500,
                                            color: '#9a9a9a', backgroundColor: '#2a2a2a', border: '1px solid #3f3f3f'
                                        }}>
                                            {entry.referenceType}
                                        </Box>
                                    </TableCell>
                                    <TableCell className='text-white py-1' sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                        <span style={{ color: '#9a9a9a' }}>{entry.account}</span>
                                        <span style={{ color: '#3f3f3f', margin: '0 4px' }}>→</span>
                                        <span style={{ color: '#9a9a9a' }}>{entry.counterparty}</span>
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Box>
                                            <Typography
                                                fontSize={13} fontWeight={700}
                                                color={entry.type === 'CREDIT' ? '#10b981' : '#ef4444'}
                                            >
                                                {formatCurrencyAmount(entry)}
                                            </Typography>
                                            {(entry.referenceType === 'ORDER' || entry.referenceType === 'ADJUSTMENT') && entry.orderFee > 0 && (
                                                <Typography fontSize={10} color="#9a9a9a">
                                                    fee: ${formatAmount(entry.orderFee + entry.platformFee)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    {/* <TableCell className='py-1' sx={{ whiteSpace: 'nowrap', color: '#9a9a9a', fontFamily: 'monospace', fontSize: '12px' }}>
                                        ${formatAmount(entry.runningBalance)}
                                    </TableCell> */}
                                    <TableCell className='py-1' sx={{ color: '#9a9a9a', fontSize: '12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={entry.description} arrow>
                                            <span>{entry.description}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className='py-1'>{getStatusChip(entry.status)}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#6f6f6f' }}>
                                        <img src={LedgerLogo} height="40px" alt="ledger" style={{ opacity: 0.3, marginBottom: 8 }} />
                                        <Typography color="#6f6f6f" fontSize={14}>No ledger entries found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    className='bg-dark-color'
                    component="div"
                    count={totalRows}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                    sx={paginationStyle}
                />
            </Box>
            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default Ledgers

// const searchFieldStyle = {
//     width: 320, background: 'transparent', borderRadius: '7px',
//     border: '1px solid #898989', transition: 'border-color 0.3s',
//     '&:hover': { border: '1px solid #ff6a3d' },
//     '&:focus-within': { border: '1px solid #ff6a3d' },
//     '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
//     '& input': { color: '#fff' },
//     '& input::placeholder': { color: '#999', opacity: 1 },
// }

const autocompleteFieldStyle = {
    '& .MuiInputBase-root': { background: 'transparent', borderRadius: '8px', border: '1px solid #898989', transition: 'border-color 0.3s' },
    '& .MuiInputBase-root:hover': { border: '1px solid #ff6a3d' },
    '& .MuiInputBase-root.Mui-focused': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input::placeholder': { color: '#999', opacity: 1 },
}

const paginationStyle = {
    color: '#898989', borderTop: '1px solid #2a2a2a',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#898989', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#898989', margin: 0 },
    '& .MuiSelect-select': { color: '#898989' },
    '& .MuiSelect-icon': { color: '#898989' },
    '& .MuiIconButton-root': { color: '#898989' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#555' },
}